import {
  IReactionDisposer,
  makeObservable,
  observable,
  reaction,
  runInAction,
} from 'mobx'

import ChatService from '~/client/src/shared/services/ChatService/Chat.service'
import StruxHubAuthService from '~/client/src/shared/services/StruxHubAuthService'
import CompaniesStore from '~/client/src/shared/stores/domain/Companies.store'
import ProjectMembersStore from '~/client/src/shared/stores/domain/ProjectMembers.store'
import ProjectRolesStore from '~/client/src/shared/stores/domain/ProjectRoles.store'
import UserProjectsStore from '~/client/src/shared/stores/domain/UserProjects.store'
import InitialState from '~/client/src/shared/stores/InitialState'

import { ImageUploader } from '../core/ports'
import { Chat, Contact } from '../core/protocol/types'
import { ChatSession } from '../core/stores/Session'
import { acquireChatSession } from './composition'
import { WebContactDirectory } from './directory'
import { createImageUploader } from './imageUpload'

export type ChatScreen =
  | { readonly kind: 'inbox' }
  | { readonly kind: 'conversation'; readonly chat: Chat }
  | { readonly kind: 'newChat' }
  | { readonly kind: 'draft'; readonly recipient: Contact }
  | { readonly kind: 'groupForm'; readonly chat?: Chat }

export interface ChatShellDeps {
  state: InitialState
  struxHubAuthService: StruxHubAuthService
  chatService: ChatService
  projectMembersStore: ProjectMembersStore
  userProjectsStore: UserProjectsStore
  companiesStore: CompaniesStore
  projectRolesStore: ProjectRolesStore
}

// The surface-independent half of a chat shell: which screen is showing, how
// the session comes to exist, and how outside signals (the profile "Message"
// button, mention taps) move between screens. Composition roots own only the
// chrome around it — the mobile view, the desktop sidebar panel.
export class ChatShellStore {
  public session: ChatSession = null
  public screen: ChatScreen = { kind: 'inbox' }

  public readonly directory: WebContactDirectory
  public readonly uploadImage: ImageUploader

  private disposers: IReactionDisposer[] = []

  public constructor(private readonly deps: ChatShellDeps) {
    makeObservable(this, {
      session: observable.ref,
      screen: observable.ref,
    })
    this.directory = new WebContactDirectory(
      deps.projectMembersStore,
      deps.userProjectsStore,
      deps.companiesStore,
      deps.projectRolesStore,
    )
    this.uploadImage = createImageUploader(deps.state.graphClient)
  }

  public mount(): void {
    const { state, struxHubAuthService, chatService } = this.deps

    this.disposers = [
      reaction(
        () => state.user?.id,
        userId => {
          if (userId) {
            const session = acquireChatSession({
              userId,
              getToken: struxHubAuthService.getValidAccessToken,
              onReadStateChanged: () =>
                chatService.refreshAllUnreadMessagesCount(),
            })
            runInAction(() => {
              this.session = session
            })
            this.consumeChatInitSignal()
          }
        },
        { fireImmediately: true },
      ),
      // The chat-init signal can be set while the shell is already mounted
      // (the user card's Chat button); it must be observed, not just read.
      reaction(
        () => state.recipientIdForChatInit,
        recipientId => {
          if (recipientId) {
            this.consumeChatInitSignal()
          }
        },
      ),
    ]
  }

  public unmount(): void {
    this.disposers.forEach(dispose => dispose())
    this.disposers = []
  }

  public setScreen = (screen: ChatScreen): void => {
    runInAction(() => {
      this.screen = screen
    })
  }

  public openConversation = (chat: Chat): void => {
    this.setScreen({ kind: 'conversation', chat })
  }

  public backToInbox = (): void => {
    this.setScreen({ kind: 'inbox' })
  }

  public openUserCard = (mentionedUserId: string): void => {
    if (this.directory.getProjectMember(mentionedUserId)) {
      this.deps.state.setIdOfUserDisplayedOnGlobalCard(mentionedUserId)
    }
  }

  public openWorkflow = (workflowId: string): void => {
    this.deps.state.setWorkflowIdDisplayedOnGlobalCard(workflowId)
  }

  // The profile "Message" button hands over just a user id; it resolves
  // through the same path as the picker.
  private async consumeChatInitSignal(): Promise<void> {
    const { state } = this.deps
    const recipientId = state.recipientIdForChatInit
    if (!recipientId || !this.session) {
      return
    }
    state.resetRecipientIdForChatInit()

    const recipient = this.directory.toContact(recipientId)
    if (!recipient) {
      return
    }

    const resolution = await this.session.resolveDirectChat(recipientId)
    if (resolution.kind === 'existing') {
      this.setScreen({ kind: 'conversation', chat: resolution.chat })
    } else if (resolution.kind === 'draft') {
      this.setScreen({ kind: 'draft', recipient })
    }
  }
}
